/**
 * HeroRenderer — hero
 * Large hero block: eyebrow + massive heading + optional body + optional badge.
 */
import React from "react";
import { Title } from "../../ui/Title";
import { Text } from "../../ui/Text";
import { Eyebrow } from "../../ui/Eyebrow";
import { EditableText } from "../EditableText";
import { AnimatedField } from "../../animations/AnimatedField";
import { resolveText, resolveTextOptional } from "../../utils/resolveText";

function fieldAnims(field) {
  if (!field || typeof field !== "object") return [null, null];
  return [field.boxAnimation ?? null, field.boxExitAnimation ?? null];
}

export function HeroRenderer({ element, editing, onChange, playing, isPaused, currentTimeSec }) {
  const heading = resolveText(element.heading);
  const eyebrow = element.eyebrow ? resolveTextOptional(element.eyebrow) : null;
  const body = element.body ? resolveTextOptional(element.body) : null;
  const badge = element.badge ? resolveTextOptional(element.badge) : null;

  const [eyebrowBox, eyebrowExit] = fieldAnims(element.eyebrow);
  const [headingBox, headingExit] = fieldAnims(element.heading);
  const [badgeBox, badgeExit] = fieldAnims(element.badge);
  const [bodyBox, bodyExit] = fieldAnims(element.body);

  return (
    <div style={{ display: "grid", gap: "var(--space-4)", alignContent: "center" }}>
      {eyebrow !== null && (
        <AnimatedField
          boxAnimation={eyebrowBox}
          boxExitAnimation={eyebrowExit}
          playing={playing}
          isPaused={isPaused}
          currentTimeSec={currentTimeSec}
        >
          <Eyebrow>
            <EditableText
              value={eyebrow || ""}
              editing={editing}
              onChange={(val) => onChange("eyebrow", val)}
            />
          </Eyebrow>
        </AnimatedField>
      )}

      <AnimatedField
        boxAnimation={headingBox}
        boxExitAnimation={headingExit}
        playing={playing}
        isPaused={isPaused}
        currentTimeSec={currentTimeSec}
      >
        <Title size="4xl">
          <EditableText
            value={heading || ""}
            editing={editing}
            onChange={(val) => onChange("heading", val)}
          />
        </Title>
      </AnimatedField>

      {badge !== null && (
        <AnimatedField
          boxAnimation={badgeBox}
          boxExitAnimation={badgeExit}
          playing={playing}
          isPaused={isPaused}
          currentTimeSec={currentTimeSec}
        >
          <span
            style={{
              display: "inline-block",
              padding: "4px 16px",
              borderRadius: "var(--radius-full)",
              background: "var(--color-bg-accent)",
              color: "var(--color-text-primary)",
              fontSize: "var(--text-sm)",
              fontWeight: 600,
              alignSelf: "start",
            }}
          >
            <EditableText
              value={badge || ""}
              editing={editing}
              onChange={(val) => onChange("badge", val)}
            />
          </span>
        </AnimatedField>
      )}

      {body !== null && (
        <AnimatedField
          boxAnimation={bodyBox}
          boxExitAnimation={bodyExit}
          playing={playing}
          isPaused={isPaused}
          currentTimeSec={currentTimeSec}
        >
          <Text role="large">
            <EditableText
              value={body || ""}
              editing={editing}
              onChange={(val) => onChange("body", val)}
            />
          </Text>
        </AnimatedField>
      )}
    </div>
  );
}

export default HeroRenderer;
